import React from "react";
import type { TFunction } from "i18next";

export interface DashboardStatLabel {
  label: string;
  icon: React.ReactNode;
  color: string;
}

const statIconWrapper = (children: React.ReactNode) => (
  <div className="w-6 h-6 p-1 rounded-md flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
    {children}
  </div>
);

export const getDashboardStatLabels = (
  t: TFunction<"translation", undefined>,
): DashboardStatLabel[] => [
  {
    label: t("dashboard.stats.total_users"),
    icon: statIconWrapper(
      <svg
        width="18"
        height="17"
        viewBox="0 0 18 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.8076 8.12297L13.1618 9.47713L15.8701 6.7688"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.8083 14.8938V13.5396C11.8083 12.8213 11.5229 12.1325 11.015 11.6246C10.5071 11.1166 9.81823 10.8313 9.09993 10.8313H5.03743C4.31914 10.8313 3.63027 11.1166 3.12235 11.6246C2.61444 12.1325 2.3291 12.8213 2.3291 13.5396V14.8938"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.06868 8.12297C8.56446 8.12297 9.77702 6.9104 9.77702 5.41463C9.77702 3.91886 8.56446 2.7063 7.06868 2.7063C5.57291 2.7063 4.36035 3.91886 4.36035 5.41463C4.36035 6.9104 5.57291 8.12297 7.06868 8.12297Z"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
    ),
    color: "border-gray-200 dark:border-gray-700 bg-emerald-50 dark:bg-emerald-900/20",
  },
  {
    label: t("dashboard.stats.new_users_this_week"),
    icon: statIconWrapper(
      <svg
        width="17"
        height="17"
        viewBox="0 0 17 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.208 8.12297L12.5622 9.47713L15.2705 6.7688"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.2087 14.8938V13.5396C11.2087 12.8213 10.9233 12.1325 10.4154 11.6246C9.9075 11.1166 9.21862 10.8313 8.50033 10.8313H4.43783C3.71953 10.8313 3.03066 11.1166 2.52274 11.6246C2.01483 12.1325 1.72949 12.8213 1.72949 13.5396V14.8938"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.46908 8.12297C7.96485 8.12297 9.17741 6.9104 9.17741 5.41463C9.17741 3.91886 7.96485 2.7063 6.46908 2.7063C4.9733 2.7063 3.76074 3.91886 3.76074 5.41463C3.76074 6.9104 4.9733 8.12297 6.46908 8.12297Z"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
    ),
    color: "border-gray-200 dark:border-gray-700 bg-emerald-50 dark:bg-emerald-900/20",
  },
  {
    label: t("dashboard.stats.events_this_week"),
    icon: statIconWrapper(
      <svg
        width="18"
        height="17"
        viewBox="0 0 18 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.19141 2.02917V4.73751"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.6074 2.02917V4.73751"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.639 3.38342H4.15983C3.41195 3.38342 2.80566 3.9897 2.80566 4.73759V14.2168C2.80566 14.9646 3.41195 15.5709 4.15983 15.5709H13.639C14.3869 15.5709 14.9932 14.9646 14.9932 14.2168V4.73759C14.9932 3.9897 14.3869 3.38342 13.639 3.38342Z"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.80566 7.44592H14.9932"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.86816 11.5083L8.22233 12.8625L10.9307 10.1542"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
    ),
    color: "border-gray-200 dark:border-gray-700 bg-emerald-50 dark:bg-emerald-900/20",
  },
  {
    label: t("dashboard.stats.surveys_this_week"),
    icon: statIconWrapper(
      <svg
        width="17"
        height="17"
        viewBox="0 0 17 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.3314 2.02917H6.26888C5.89494 2.02917 5.5918 2.33232 5.5918 2.70626V4.06042C5.5918 4.43437 5.89494 4.73751 6.26888 4.73751H10.3314C10.7053 4.73751 11.0085 4.43437 11.0085 4.06042V2.70626C11.0085 2.33232 10.7053 2.02917 10.3314 2.02917Z"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.0078 3.38342H12.362C12.7211 3.38342 13.0656 3.52609 13.3195 3.78005C13.5735 4.034 13.7161 4.37844 13.7161 4.73759V14.2168C13.7161 14.5759 13.5735 14.9203 13.3195 15.1743C13.0656 15.4283 12.7211 15.5709 12.362 15.5709H4.23698C3.87783 15.5709 3.53339 15.4283 3.27944 15.1743C3.02548 14.9203 2.88281 14.5759 2.88281 14.2168V4.73759C2.88281 4.37844 3.02548 4.034 3.27944 3.78005C3.53339 3.52609 3.87783 3.38342 4.23698 3.38342H5.59115"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.2998 8.12292H11.0081"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.2998 11.5084H11.0081"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.5918 8.12292H5.59857"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.5918 11.5084H5.59857"
          stroke="#00C49A"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
    ),
    color: "border-gray-200 dark:border-gray-700 bg-emerald-50 dark:bg-emerald-900/20",
  },
  {
    label: t("dashboard.stats.active_coupons"),
    icon: statIconWrapper(
      <svg
        width="17"
        height="17"
        viewBox="0 0 17 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.1159 6.09167H3.28255C2.90861 6.09167 2.60547 6.39482 2.60547 6.76876V8.12292C2.60547 8.49687 2.90861 8.80001 3.28255 8.80001H14.1159C14.4898 8.80001 14.793 8.49687 14.793 8.12292V6.76876C14.793 6.39482 14.4898 6.09167 14.1159 6.09167Z"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.69922 6.09167V14.8938"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.4391 8.80005V13.5396C13.4391 13.8988 13.2965 14.2432 13.0425 14.4972C12.7885 14.7511 12.4441 14.8938 12.085 14.8938H5.31413C4.95498 14.8938 4.61054 14.7511 4.35659 14.4972C4.10263 14.2432 3.95996 13.8988 3.95996 13.5396V8.80005"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.65267 6.09177C5.20374 6.09177 4.77319 5.91343 4.45574 5.59598C4.1383 5.27854 3.95996 4.84799 3.95996 4.39906C3.95996 3.95012 4.1383 3.51958 4.45574 3.20213C4.77319 2.88469 5.20374 2.70635 5.65267 2.70635C6.30584 2.69497 6.94592 3.01189 7.48942 3.61578C8.03292 4.21967 8.45463 5.08251 8.69954 6.09177C8.94446 5.08251 9.36617 4.21967 9.90967 3.61578C10.4532 3.01189 11.0932 2.69497 11.7464 2.70635C12.1954 2.70635 12.6259 2.88469 12.9433 3.20213C13.2608 3.51958 13.4391 3.95012 13.4391 4.39906C13.4391 4.84799 13.2608 5.27854 12.9433 5.59598C12.6259 5.91343 12.1954 6.09177 11.7464 6.09177"
          stroke="#00C49A"
          strokeWidth="1.35417"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
    ),
    color: "border-gray-200 dark:border-gray-700 bg-emerald-50 dark:bg-emerald-900/20",
  },
  {
    label: t("dashboard.stats.total_community_partners"),
    icon: statIconWrapper(
      <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.75 8.125C8.24277 8.125 9.45312 6.91465 9.45312 5.42188C9.45312 3.9291 8.24277 2.71875 6.75 2.71875C5.25723 2.71875 4.04688 3.9291 4.04688 5.42188C4.04688 6.91465 5.25723 8.125 6.75 8.125Z" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2.6875 14.875V13.5312C2.6875 12.8145 2.9722 12.1271 3.47903 11.6203C3.98586 11.1135 4.67329 10.8281 5.39062 10.8281H8.10938C8.82671 10.8281 9.51414 11.1135 10.021 11.6203C10.5278 12.1271 10.8125 12.8145 10.8125 13.5312V14.875" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.8438 3.39062C13.4253 3.6317 13.8281 4.20375 13.8281 4.875C13.8281 5.54625 13.4253 6.1183 12.8438 6.35938M15.3125 14.875V13.5312C15.3091 12.9331 15.1069 12.3531 14.7375 11.8826C14.368 11.4122 13.8525 11.0783 13.2719 10.9336" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
      </svg>,
    ),
    color: "border-gray-200 dark:border-gray-700 bg-emerald-50 dark:bg-emerald-900/20",
  },
];
